// src/pages/DraftWars/draftWarsEngine.js
// All Firestore operations for Draft Wars. Keeps the pages focused on UI.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  writeBatch,
  runTransaction,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { SEED_CAPTAINS, SEED_PLAYERS, SESSION_DEFAULTS } from "./draftWarsData";
import { serverNow, syncServerTime } from "./draftWarsHooks";

// ─── Layout ─────────────────────────────────────────────────────────────────
// draftWars/session (main doc)
//   - settings{}, status, currentAuction{}, stats
// draftWars/captains/{id}
//   - name, image, color, token, budgetRemaining, roster[]
// draftWars/players/{id}
//   - name, tier, basePrice, status, soldTo, soldFor
// draftWars/bids/{auto}
//   - captainId, playerId, amount, at

const ROOT = "draftWars";
const SESSION_DOC = doc(db, ROOT, "session");
const CAPTAINS_COL = collection(db, ROOT, "session", "captains");
const PLAYERS_COL = collection(db, ROOT, "session", "players");
const BIDS_COL = collection(db, ROOT, "session", "bids");

// Generate a random URL-safe token (9 chars)
export function generateToken() {
  const alphabet = "abcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 9; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

// ─── SESSION LIFECYCLE ──────────────────────────────────────────────────────

// Create (or reset) the main session. Wipes existing captains/players/bids.
export async function createSession() {
  // Clear subcollections first
  const [capSnap, playerSnap, bidSnap] = await Promise.all([
    getDocs(CAPTAINS_COL),
    getDocs(PLAYERS_COL),
    getDocs(BIDS_COL),
  ]);
  const batch = writeBatch(db);
  capSnap.forEach((d) => batch.delete(d.ref));
  playerSnap.forEach((d) => batch.delete(d.ref));
  bidSnap.forEach((d) => batch.delete(d.ref));

  // Seed captains with fresh tokens
  SEED_CAPTAINS.forEach((c) => {
    batch.set(doc(CAPTAINS_COL, c.id), {
      id: c.id,
      name: c.name,
      color: c.color,
      token: generateToken(),
      budgetRemaining: SESSION_DEFAULTS.budget,
      roster: [], // array of playerIds
    });
  });

  // Seed players
  SEED_PLAYERS.forEach((p) => {
    batch.set(doc(PLAYERS_COL, p.id), {
      ...p,
      status: "available", // "available" | "active" | "sold"
      soldTo: null,
      soldFor: null,
    });
  });

  // Main session doc
  batch.set(SESSION_DOC, {
    status: "setup", // "setup" | "active" | "complete"
    settings: { ...SESSION_DEFAULTS },
    currentAuction: null, // {playerId, highBid, highBidder, timerEndAt, phase}
    createdAt: serverTimestamp(),
  });

  await batch.commit();
}

// Regenerate tokens for all captains (in case one leaks)
export async function regenerateAllTokens() {
  const snap = await getDocs(CAPTAINS_COL);
  const batch = writeBatch(db);
  snap.forEach((d) => batch.update(d.ref, { token: generateToken() }));
  await batch.commit();
}

// Regenerate a single captain's token
export async function regenerateCaptainToken(captainId) {
  await updateDoc(doc(CAPTAINS_COL, captainId), { token: generateToken() });
}

// Mark the draft as active (after setup)
export async function startDraft() {
  await updateDoc(SESSION_DOC, { status: "active" });
}

// Mark the draft complete
export async function completeDraft() {
  await updateDoc(SESSION_DOC, { status: "complete", currentAuction: null });
}

// ─── AUCTION FLOW ───────────────────────────────────────────────────────────

// Nominate a player — puts them on the block in PREVIEW mode.
// Captains can see who's up but can't bid until admin clicks Start Bidding.
export async function nominatePlayer(playerId) {
  const playerRef = doc(PLAYERS_COL, playerId);
  const snap = await getDoc(playerRef);
  if (!snap.exists()) throw new Error("Player not found");
  const player = snap.data();
  if (player.status !== "available") {
    throw new Error(`Player is ${player.status}, cannot nominate.`);
  }
  const batch = writeBatch(db);
  batch.update(playerRef, { status: "active" });
  batch.update(SESSION_DOC, {
    currentAuction: {
      playerId,
      playerName: player.name,
      tier: player.tier,
      basePrice: player.basePrice,
      highBid: null,
      highBidder: null,
      highBidderName: null,
      timerEndAt: null,
      // Phases: "nominated" -> "waiting_for_bid" -> "active" -> "ended"
      phase: "nominated",
    },
  });
  await batch.commit();
  return player;
}

// Admin clicks Start Bidding — opens the auction to captain bids.
export async function startBidding() {
  await updateDoc(SESSION_DOC, {
    "currentAuction.phase": "waiting_for_bid",
  });
}

// Pick a random available player and nominate them
export async function randomNominate() {
  const snap = await getDocs(PLAYERS_COL);
  const available = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => p.status === "available");
  if (available.length === 0) throw new Error("No available players left");
  const pick = available[Math.floor(Math.random() * available.length)];
  return nominatePlayer(pick.id);
}

// Cancel the current nomination (returns player to pool, clears auction)
export async function cancelNomination() {
  const snap = await getDoc(SESSION_DOC);
  const auction = snap.data()?.currentAuction;
  if (!auction) return;
  const batch = writeBatch(db);
  if (auction.playerId) {
    batch.update(doc(PLAYERS_COL, auction.playerId), { status: "available" });
  }
  batch.update(SESSION_DOC, { currentAuction: null });
  await batch.commit();
}

// Place a bid — transactional, validates everything
export async function placeBid(captainToken, amount) {
  // Make sure we have a fresh server-time offset before computing timerEndAt
  // (throttled internally — only probes if last sync > 60s ago)
  await syncServerTime();
  return runTransaction(db, async (tx) => {
    // Find the captain by token
    const capSnap = await getDocs(CAPTAINS_COL);
    const captainDoc = capSnap.docs.find((d) => d.data().token === captainToken);
    if (!captainDoc) throw new Error("Invalid token");
    const captain = captainDoc.data();

    const sessionSnap = await tx.get(SESSION_DOC);
    if (!sessionSnap.exists()) throw new Error("No session");
    const session = sessionSnap.data();
    const auction = session.currentAuction;
    if (!auction) throw new Error("No active auction");
    if (auction.phase === "nominated") throw new Error("Admin hasn't opened bidding yet");
    if (auction.phase === "ended") throw new Error("Auction has ended");

    // Timer check — if timer is running, reject bids past the deadline
    if (auction.timerEndAt && auction.timerEndAt.toMillis() < serverNow()) {
      throw new Error("Auction timer has expired");
    }

    // Bid amount validation
    const settings = session.settings || SESSION_DEFAULTS;
    const minBid = auction.highBid
      ? auction.highBid + settings.bidIncrement
      : auction.basePrice;
    if (amount < minBid) {
      throw new Error(`Minimum bid is $${minBid.toLocaleString()}`);
    }

    // Can't outbid yourself
    if (auction.highBidder === captain.id) {
      throw new Error("You are already the high bidder");
    }

    // Roster & budget check — captains can spend up to their FULL remaining budget
    const slotsLeft = settings.rosterSize - (captain.roster?.length || 0);
    if (slotsLeft <= 0) {
      throw new Error("Your roster is full");
    }
    if (amount > captain.budgetRemaining) {
      throw new Error(
        `Exceeds your budget (${captain.budgetRemaining.toLocaleString()} remaining)`
      );
    }

    // All good — update auction (timer is anchored to server time to stay consistent across clients)
    const newEndAt = Timestamp.fromMillis(
      serverNow() + settings.timerSeconds * 1000
    );
    tx.update(SESSION_DOC, {
      "currentAuction.highBid": amount,
      "currentAuction.highBidder": captain.id,
      "currentAuction.highBidderName": captain.name,
      "currentAuction.timerEndAt": newEndAt,
      "currentAuction.phase": "active",
    });

    // Log to bid history (outside transaction to avoid blocking)
    return { captainId: captain.id, amount, playerId: auction.playerId };
  }).then(async (result) => {
    if (result) {
      await addDoc(BIDS_COL, {
        ...result,
        at: serverTimestamp(),
      });
    }
    return result;
  });
}

// Confirm the winning bid — transfers player to captain, deducts budget
export async function confirmWinner() {
  return runTransaction(db, async (tx) => {
    const sessionSnap = await tx.get(SESSION_DOC);
    if (!sessionSnap.exists()) throw new Error("No session");
    const auction = sessionSnap.data().currentAuction;
    if (!auction) throw new Error("No active auction");
    if (!auction.highBidder) {
      // No bids — return player to pool, no winner
      tx.update(doc(PLAYERS_COL, auction.playerId), { status: "available" });
      tx.update(SESSION_DOC, { currentAuction: null });
      return { unsold: true, playerId: auction.playerId };
    }

    const captainRef = doc(CAPTAINS_COL, auction.highBidder);
    const captainSnap = await tx.get(captainRef);
    if (!captainSnap.exists()) throw new Error("Captain not found");
    const captain = captainSnap.data();

    // Mark player sold
    tx.update(doc(PLAYERS_COL, auction.playerId), {
      status: "sold",
      soldTo: auction.highBidder,
      soldFor: auction.highBid,
    });

    // Deduct budget + add to roster
    tx.update(captainRef, {
      budgetRemaining: captain.budgetRemaining - auction.highBid,
      roster: [...(captain.roster || []), auction.playerId],
    });

    // Clear auction + record lastSale for SOLD splash + sound triggers
    tx.update(SESSION_DOC, {
      currentAuction: null,
      lastSale: {
        playerId: auction.playerId,
        playerName: auction.playerName,
        tier: auction.tier,
        captainId: auction.highBidder,
        captainName: auction.highBidderName,
        amount: auction.highBid,
        soldAt: serverTimestamp(),
      },
    });

    return {
      unsold: false,
      playerId: auction.playerId,
      captainId: auction.highBidder,
      amount: auction.highBid,
    };
  });
}

// Undo the last confirmed sale (admin safety net)
export async function undoLastSale(playerId, captainId, amount) {
  const batch = writeBatch(db);
  const captainRef = doc(CAPTAINS_COL, captainId);
  const captainSnap = await getDoc(captainRef);
  if (!captainSnap.exists()) return;
  const captain = captainSnap.data();
  batch.update(captainRef, {
    budgetRemaining: captain.budgetRemaining + amount,
    roster: (captain.roster || []).filter((id) => id !== playerId),
  });
  batch.update(doc(PLAYERS_COL, playerId), {
    status: "available",
    soldTo: null,
    soldFor: null,
  });
  await batch.commit();
}

// ─── LOOKUP HELPERS ─────────────────────────────────────────────────────────

export async function findCaptainByToken(token) {
  const snap = await getDocs(CAPTAINS_COL);
  const match = snap.docs.find((d) => d.data().token === token);
  return match ? { id: match.id, ...match.data() } : null;
}

// Max a captain can bid right now — full remaining budget (no reserve cap).
// They're free to blow it all on one player if they want.
export function calcMaxBid(captain, settings) {
  const slotsLeft = settings.rosterSize - (captain.roster?.length || 0);
  if (slotsLeft <= 0) return 0;
  return Math.max(0, captain.budgetRemaining);
}

// Next-bid amount based on current auction state
export function nextBidAmount(auction, settings) {
  if (!auction) return 0;
  return auction.highBid
    ? auction.highBid + (settings?.bidIncrement || 1000)
    : auction.basePrice;
}

// Refs for components that want to set up listeners
export const refs = {
  session: SESSION_DOC,
  captains: CAPTAINS_COL,
  players: PLAYERS_COL,
  bids: BIDS_COL,
};
